"use client";
import React, { useRef, useEffect } from "react";
import Carousel from "react-multi-carousel";
import { CarouselInternalState } from "react-multi-carousel/lib/types";
import "react-multi-carousel/lib/styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft
} from "@fortawesome/pro-solid-svg-icons";

import { getDotColor } from "./sourceMapDots";
import type { CarouselProps, CustomArrowProps } from "./carouselMulti.types";

import "./styles.scss";

export const CustomRightArrow = ({
  onClick,
  carouselState,
  outlined = true,
  variantButton
}: CustomArrowProps) => {
  const { currentSlide, totalItems, slidesToShow } =
    carouselState as CarouselInternalState;

  return (
    <button
      onClick={onClick}
      className={`arrow-right btn btn-md btn-icon-rounded ${outlined ? "outlined" : ""} ${variantButton ? variantButton : ""}`}
      disabled={!(currentSlide + slidesToShow < totalItems)}
    >
      <FontAwesomeIcon fontSize={16} icon={faChevronRight} />
    </button>
  );
};

export const CustomLeftArrow = ({
  onClick,
  carouselState,
  outlined = true,
  variantButton
}: CustomArrowProps) => {
  const { currentSlide } = carouselState as CarouselInternalState;

  return (
    <button
      onClick={onClick}
      className={`arrow-left btn btn-md btn-icon-rounded ${outlined ? "outlined" : ""} ${variantButton ? variantButton : ""}`}
      disabled={!(currentSlide > 0)}
    >
      <FontAwesomeIcon fontSize={16} icon={faChevronLeft} />
    </button>
  );
};

const CarouselMulti = ({
  arrows = true,
  autoPlaySpeed = 3000,
  children,
  dotListClass = "",
  infinite = false,
  removeArrows = true,
  renderArrowsWhenDisabled = true,
  showDots = false,
  slidesDesk = 1,
  slidesMobile = 1,
  slidesTablet = 1,
  customButtonGroup,
  initialSlide,
  removeArrowOnDeviceType,
  responsiveCustom,
  variantButton,
  variantDots = "lemon"
}: CarouselProps) => {
  const carousel = useRef<any>(null);
  const dotColor = variantDots && getDotColor(variantDots);

  /**
   * Solução encontrada para abrir o carrossel em um slide especifico:
   * Link: https://github.com/YIZHUANG/react-multi-carousel/issues/96
   */
  useEffect(() => {
    if (typeof initialSlide === "number") {
      carousel?.current?.goToSlide(initialSlide);
    }
  }, [initialSlide]);

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1025 },
      items: slidesDesk
    },
    tablet: {
      breakpoint: { max: 1024, min: 465 },
      items: slidesTablet
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: slidesMobile
    }
  };

  return (
    <div className="wrapper">
      <style>
        {`
          :root {
            --active-dot-color: ${dotColor};
          }
        `}
      </style>
      <Carousel
        ref={carousel}
        arrows={arrows}
        autoPlaySpeed={autoPlaySpeed}
        customRightArrow={<CustomRightArrow variantButton={variantButton} />}
        customLeftArrow={<CustomLeftArrow variantButton={variantButton} />}
        infinite={infinite}
        keyBoardControl
        removeArrowOnDeviceType={
          removeArrowOnDeviceType
            ? removeArrowOnDeviceType
            : removeArrows
              ? ["tablet", "mobile"]
              : []
        }
        renderArrowsWhenDisabled={renderArrowsWhenDisabled}
        responsive={responsiveCustom || responsive}
        showDots={showDots}
        swipeable
        dotListClass={`hide-dot-list ${dotListClass}`}
        customButtonGroup={customButtonGroup}
      >
        {children}
      </Carousel>
    </div>
  );
};

export default CarouselMulti;
